import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { ActiveSupportSessionBanner } from './ActiveSupportSessionBanner';
import { VerificationBanner } from './VerificationBanner';
import { WorkstationPrompt } from './WorkstationPrompt';
import WaWiSupportButton from '../support/WaWiSupportButton';
import { BackgroundTaskIndicator } from './BackgroundTaskIndicator';
import { PermissionGate } from '../common/PermissionGate';
import { useFirmendatenPflicht } from '../../hooks/useFirmendatenPflicht';
import { useScrollWiederherstellung } from '../../hooks/useScrollWiederherstellung';
import { FirmendatenBanner } from './FirmendatenBanner';
import { useState, useEffect, useRef } from 'react';
import { useShopifyAutoSync } from '../../hooks/useShopifyAutoSync';
import { trackPageVisit } from '../../hooks/usePageUsageTracker';
import { initErrorCollector } from '../../utils/errorCollector';
import { api } from '../../api/client';
import { useAuthStore } from '../../store/useAuthStore';
import { useCurrencyStore } from '../../store/useCurrencyStore';

// Initialize global error collector once
initErrorCollector();

export function Layout() {
  // Global auto-sync: runs in background regardless of which page is open
  useShopifyAutoSync();

  // Load user data from /me if not yet in auth store
  const { user, setUser, setPriceFocus } = useAuthStore();
  const meLoaded = useRef(false);
  useEffect(() => {
    if (meLoaded.current) return;
    meLoaded.current = true;
    api.get<{ user: { timezone?: string; tenantTimezone?: string; language?: string; googleId?: string | null; hasUserPassword?: boolean }; priceFocus?: { groupKey: string; groupName: string | null; ist_standard?: boolean } | null }>('/me')
      .then((res) => {
        if (res?.user && user) {
          setUser({
            ...user,
            timezone: res.user.timezone || user.timezone,
            // Zeitzone der Firma — Grundlage aller Datumsanzeigen (siehe dateUtils).
            tenantTimezone: res.user.tenantTimezone || user.tenantTimezone,
            language: res.user.language || user.language,
            googleId: res.user.googleId,
            hasUserPassword: res.user.hasUserPassword,
          });
        }
        // Preisfokus (TICK-2026-01108) — analog zu activeWarehouse, hier direkt aus der
        // ohnehin geladenen /me-Antwort statt eines eigenen Requests.
        if (res && 'priceFocus' in res) {
          setPriceFocus(res.priceFocus ?? null);
        }
      })
      .catch(() => {}); // Silently ignore - fallback timezone in dateUtils
  }, []);

  // Load currency from company settings if not yet in store
  const currencyLoaded = useRef(false);
  useEffect(() => {
    if (currencyLoaded.current) return;
    currencyLoaded.current = true;
    api.get<{ settings: Record<string, unknown> | null }>('/settings/company')
      .then((res) => {
        const currency = (res?.settings?.currency as string) || 'EUR';
        // Firmeneinstellungen speichern das Land als `countryCode`; `country` ist nur ein
        // Alt-Fallback. Vorher wurde nur `country` gelesen → Land kam nie aus den Settings.
        const country = (res?.settings?.countryCode as string) || (res?.settings?.country as string) || 'DE';
        useCurrencyStore.getState().setCurrency(currency);
        useCurrencyStore.getState().setCountry(country);
      })
      .catch(() => {}); // Silently ignore - default EUR from store
  }, []);

  // BISPrint: diesen PC zentral anmelden, damit der Admin ihm in den Druck-Einstellungen einen
  // Drucker-Client zuordnen kann (Geräte-ID-Header wird automatisch mitgesendet).
  //
  // Der Herzschlag alle 5 Minuten ist die Voraussetzung dafür, dass die Arbeitsplatz-Liste
  // „gerade verbunden" ehrlich beantworten kann: Vorher meldete sich der Browser genau EINMAL je
  // Seitenladen — wer drei Stunden ohne Neuladen arbeitete, sah aus wie seit drei Stunden weg.
  // Nur bei sichtbarem Tab, damit ein vergessenes Hintergrundfenster keinen Arbeitsplatz als
  // besetzt meldet (und die Datenbank nicht für nichts arbeitet).
  useEffect(() => {
    const announce = () => {
      if (document.visibilityState !== 'visible') return;
      api.post('/bisprint/device/announce', {}).catch(() => {}); // best effort, fire-and-forget
    };
    announce();
    const interval = window.setInterval(announce, 5 * 60 * 1000);
    document.addEventListener('visibilitychange', announce);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', announce);
    };
  }, []);

  // Track page visits for dashboard "Frequently Used"
  const location = useLocation();
  useEffect(() => {
    trackPageVisit(location.pathname);
  }, [location.pathname]);

  // MDE-Routen (Handscanner) laufen "App-artig" ohne Sidebar/Header und ohne Padding,
  // damit auf kleinen Displays maximal Platz für den eigentlichen Workflow bleibt.
  // Scroll-Position je Seite merken. Der Haken sitzt am scrollenden `<main>` — NICHT am
  // Fenster: Der Inhalt liegt in einem `overflow-auto`-Container, `window.scrollY` bleibt
  // hier dauerhaft 0. Gilt fuer beide Zweige (MDE und Standard) ueber denselben Ref.
  const inhaltRef = useRef<HTMLElement>(null);
  useScrollWiederherstellung(inhaltRef);

  const isMde = location.pathname.includes('/mde')
             || location.pathname.includes('/pick-lists/');

  // Einstellungsbereich des Mandanten (`/settings` und alles darunter). Bewusst am Pfadanfang
  // geprüft und nicht mit `includes()`: Sonst träfe die Regel auch `/admin/settings`,
  // `/pos/settings` und jede andere Seite, die das Wort im Pfad trägt.
  const istEinstellungsbereich = location.pathname === '/settings'
                              || location.pathname.startsWith('/settings/');

  // Firmendaten-Pflicht: Wer sie pflegen darf, wird zur Firmenmaske geführt, bis sie
  // vollständig ist. 🚨 Umleiten, NICHT sperren (Ben, 28.08.2026) — die API bleibt offen, und
  // wer nicht speichern darf, arbeitet weiter und sieht nur den Hinweis.
  const { stand: firmendaten, mussPflegen, zeigeHinweis } = useFirmendatenPflicht();

  // 🚨 Die Zielseite selbst muss ausgenommen sein, sonst navigiert sich die App zu Tode.
  // `/settings` ohne Parameter zeigt bereits den Firmenabschnitt (`activeSection` startet auf
  // 'company'), deshalb genügt der Pfad — ein zusätzlicher Parameter-Vergleich würde die
  // Umleitung bei jedem Klick auf einen anderen Abschnitt erneut auslösen und den Anwender
  // aus den Einstellungen aussperren, in denen er gerade arbeitet.
  const istAufDerZielseite = istEinstellungsbereich;
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // Close mobile sidebar when screen becomes large
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setMobileSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Prevent body scroll when mobile sidebar is open
  useEffect(() => {
    if (mobileSidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileSidebarOpen]);

  const handleToggleSidebar = () => {
    // On mobile (< lg), toggle mobile overlay
    if (window.innerWidth < 1024) {
      setMobileSidebarOpen(!mobileSidebarOpen);
    } else {
      // On desktop, toggle collapsed state
      setSidebarCollapsed(!sidebarCollapsed);
    }
  };

  // MDE-Modus: Sidebar/Header/Padding komplett weg — wie eigenständige App
  if (isMde) {
    return (
      <div className="h-screen bg-slate-50 overflow-hidden flex flex-col">
        <main ref={inhaltRef} className="flex-1 overflow-auto" data-testid="app-content">
          <Outlet />
        </main>
        <BackgroundTaskIndicator />
      </div>
    );
  }

  // 🚨 **Umleitung, keine Sperre.** Wer die Firmendaten pflegen darf und noch nicht
  // vollständig ist, landet auf der Firmenmaske — und kommt von dort weg, sobald sie steht.
  //
  // Drei Ausnahmen tragen die Regel:
  //   • `isMde` — ein Lagerist mit Scanner darf nicht auf eine Einstellungsmaske geworfen
  //     werden, die er auf dem Handgerät gar nicht bedienen kann.
  //   • `istAufDerZielseite` — sonst navigiert sich die App zu Tode.
  //   • `mussPflegen` ist erst true, wenn die Antwort da ist UND der Benutzer speichern darf
  //     (siehe `useFirmendatenPflicht`): Im Ladezustand und bei einem Netzfehler passiert
  //     nichts, und wer nicht speichern darf, stünde sonst vor einem Formular, dessen
  //     Speichern-Knopf 403 liefert.
  //
  // 🚨 **Notbremse `?pflicht=aus`.** Am 29.08.2026 sass ein Anwender dauerhaft in der Umleitung:
  // Er hatte gespeichert, aber der Hook hielt seinen Stand pro Instanz und das Layout erfuhr
  // nichts davon (behoben — siehe `useFirmendatenPflicht`). Ein Riegel ohne Ausweg ist genau
  // dann am gefährlichsten, wenn er einen Fehler hat: Der Betroffene kann sich nicht selbst
  // befreien und auch niemanden mehr um Hilfe bitten, weil er nirgendwo hinkommt.
  // Der Parameter gilt nur für diesen Seitenaufruf — er wird nirgends gespeichert.
  const pflichtAusgesetzt = new URLSearchParams(location.search).get('pflicht') === 'aus';

  if (mussPflegen && !isMde && !istAufDerZielseite && !pflichtAusgesetzt) {
    return <Navigate to={firmendaten.zielUrl} replace />;
  }

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      {/* Desktop Sidebar - hidden on mobile */}
      <div className="hidden lg:block">
        <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      </div>

      {/* Mobile Sidebar Overlay */}
      {mobileSidebarOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setMobileSidebarOpen(false)}
            aria-hidden="true"
          />
          {/* Sidebar */}
          <div className="fixed inset-y-0 left-0 z-50 lg:hidden">
            <Sidebar
              collapsed={false}
              onToggle={() => setMobileSidebarOpen(false)}
              isMobile={true}
            />
          </div>
        </>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <ActiveSupportSessionBanner />
        <VerificationBanner />
        {/* Für Benutzer ohne Recht „Einstellungen bearbeiten": Hinweis statt Umleitung. */}
        {zeigeHinweis && <FirmendatenBanner fehlend={firmendaten.fehlendKlartext} />}
        {/* Fragt einmalig nach dem Arbeitsplatz — nur bei mehreren BISPrint-Clients. */}
        <WorkstationPrompt />
        <Header onToggleSidebar={handleToggleSidebar} />
        <main ref={inhaltRef} className="flex-1 p-3 pt-4 sm:p-4 sm:pt-5 md:p-6 md:pt-7 overflow-auto" data-testid="app-content">
          {/* 🚨 **Der Riegel steht hier und nicht an den 20 Einstellungs-Routen.** Sie liegen in
              `App.tsx` flach nebeneinander (`settings`, `settings/manufacturers`,
              `settings/design-studio`, …); jede einzeln zu umschliessen hiesse, beim nächsten
              Zuwachs genau eine zu vergessen — und die eine wäre dann offen, ohne dass etwas
              rot wird. Über den Pfad gilt die Regel automatisch auch für künftige Unterseiten.

              Der Super-Admin-Bereich `/admin/settings` ist nicht betroffen: Er beginnt mit
              `/admin` und hat eine eigene Absicherung. */}
          {istEinstellungsbereich ? (
            <PermissionGate module="settings" operation="edit">
              <Outlet />
            </PermissionGate>
          ) : (
            <Outlet />
          )}
        </main>
      </div>

      {/* Floating Background Task Indicator */}
      <BackgroundTaskIndicator />

      {/* Floating Support Button */}
      <WaWiSupportButton />
    </div>
  );
}
